import { MotionConfig } from "framer-motion";
import { darkTheme, vh } from "~/ui/style/stitches.config";
import { Room } from "./pages/room/Room";
import { Route, Switch, useLocation } from "wouter";
import { ActionProvider } from "./ui/room/Actions";
import { Spreaditor } from "./pages/spreaditor/Spreaditor";
import { AuthFormState, AuthPage } from "./pages/login/Auth";
import { AuthContext } from "./utils/useAuth";
import RedirectPage, { RedirectPageWithKnownRef } from "./pages/404/Redirect";
import { TopBar } from "./ui/menus/TopBar";
import { PageNotFound } from "./pages/404/PageNotFound";
import { LoadingPage } from "./pages/loading/LoadingPage";
import { useContext, useEffect, useState } from "react";
import { RoomNotFound } from "./pages/404/RoomNotFound";
import { localState } from "~/state/state";
import { TOAST_TIME_MS } from "./utils/consts";
import { useSnapshot } from "valtio";
import Home, { MOONLIGHT_PAGE } from "./pages/Home";
import { sendPageEvent } from "./utils/analytics";
import { PostLeavingSurvey } from "./ui/menus/Survey";
import ProfilePage from "./pages/profiles/Profile";
import { BillingPage, Success } from "./pages/marketplace/Billing";
import { Oauth2Callback } from "./pages/callback/Oauth2";
import { SessionView } from "./pages/marketplace/Session";
import { SessionAction } from "./pages/callback/SessionAction";
import { clearToast } from "./utils/handleToasts";
import { MetaTagHeaders } from "./ui/components/MetaTags";
import { SessionVideoPlayer } from "./ui/components/SessionVideoPlayer";
import { DailySpreadList } from "./pages/admin/DailySpreadList";
import { GuidebookList } from "./pages/admin/GuidebookList";
import { DailySpreadPage } from "./pages/spread/DailySpreadPage";
import { CommunityFeed } from "./pages/community/CommunityFeed";
import { CommunityPostDetail } from "./pages/community/CommunityPostDetail";
import { GamePage } from "./pages/game/GamePage";
import { isIOSNativeApp } from "./utils/deviceDetection";

// auth routes are kept light-only until the auth screens are fully themed
const AUTH_ROUTES = [
  "/signin",
  "/signup",
  "/signout",
  "/forgotPassword",
  "/resetPassword",
];

// Add JS bridge for native app auth handoff
declare global {
  interface Window {
    setAuthToken?: (token: string) => void;
  }
}
window.setAuthToken = function (token: string) {
  const current = localStorage.getItem('MoonlightUser');
  if (!current || JSON.parse(current).token !== token) {
    localStorage.setItem('MoonlightUser', JSON.stringify({ token }));
    window.location.reload();
  }
};

function App() {
  const { currentMessage, isConnecting, isOnMobile, isInRoom } =
    useSnapshot(localState);

  const { user, getUser, logout } = useContext(AuthContext);
  const [location] = useLocation();
  const [messageTimer, setMessageTimer] = useState<number | null>(null);

  // On app load, if user is logged in, refresh user
  useEffect(() => {
    if (!!user) {
      getUser();
    }
  }, []);

  // For analytics / store username & info
  useEffect(() => {
    if (user && user.username) {
      localStorage.setItem("sessionId", user.username);
    }
  }, [user]);

  useEffect(() => {
    if (isOnMobile) {
      localState.isGuidebookOpen = false;
    }
  }, [isOnMobile]);

  useEffect(() => {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    ) {
      // true for mobile device
      localState.isOnMobile = true;
    } else {
      localState.isOnMobile = false;
    }
  }, [navigator.userAgent]);

  useEffect(() => {
    // default to not in room
    localState.isInRoom = false;
    if (!location.includes("/room/")) {
      localState.isConnecting = false;
    }
    sendPageEvent(location);
  }, [location]);

  // manage toasts
  useEffect(() => {
    if (currentMessage.isPerpetual) return;
    if (currentMessage.msg) {
      if (messageTimer) window.clearTimeout(messageTimer);
      const tmr = window.setTimeout(() => {
        clearToast();
      }, TOAST_TIME_MS);
      setMessageTimer(tmr);
    }
  }, [currentMessage]);

  // Fix iOS Safari keyboard bug: when the virtual keyboard closes,
  // Safari sometimes leaves window.scrollY non-zero, shifting the
  // entire page up and causing the fixed TopBar to overlap content.
  // Since all scrolling happens in inner containers, window scroll should always be 0.
  useEffect(() => {
    const handleFocusOut = () => {
      window.scrollTo(0, 0);
    };
    window.addEventListener('focusout', handleFocusOut);
    return () => window.removeEventListener('focusout', handleFocusOut);
  }, []);

  // Resolve the app-level dark theme from the user's themePreference
  // ("system" | "light" | "dark"). Logged-out users always get light, except
  // inside the iOS app, where the last applied theme is reused (cached in
  // localStorage) to avoid a flash before the user loads.
  // This is independent of `inDarkMode`, which is a room-local toggle.
  //
  // The theme class is applied to document.body (rather than a wrapper div)
  // so that content portaled directly to document.body (e.g. journal/community
  // overlays via createPortal) still inherits the theme's CSS variables.
  useEffect(() => {
    // auth routes are only partially themed, so force them light for everyone
    // (logged in or not); also stay light before an account exists. otherwise
    // honor the theme preference.
    const isAuthRoute = AUTH_ROUTES.includes(location);

    if (!user && !isAuthRoute && isIOSNativeApp()) {
      let cachedDark = false;
      try {
        cachedDark = localStorage.getItem("moonlight-app-dark") === "1";
      } catch {}
      document.body.classList.toggle(darkTheme.className, cachedDark);
      localState.appDarkMode = cachedDark;
      return;
    }

    if (!user || isAuthRoute) {
      document.body.classList.toggle(darkTheme.className, false);
      localState.appDarkMode = false;
      try {
        localStorage.setItem("moonlight-app-dark", "0");
      } catch {}
      return;
    }

    const preference = user.themePreference ?? "system";
    const systemQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const applyPreference = () => {
      const isDark =
        preference === "dark" ||
        (preference === "system" && systemQuery.matches);
      document.body.classList.toggle(darkTheme.className, isDark);
      localState.appDarkMode = isDark;
      try {
        localStorage.setItem("moonlight-app-dark", isDark ? "1" : "0");
      } catch {}
    };

    applyPreference();

    if (preference === "system") {
      systemQuery.addEventListener("change", applyPreference);
      return () => systemQuery.removeEventListener("change", applyPreference);
    }
  }, [user, user?.themePreference, location]);

  return (
    <div
      style={{
        width: "100vw",
        position: "relative",
        height: vh(100),
        overflow: "hidden",
      }}
    >
      {<MetaTagHeaders />}
      <MotionConfig
        transition={{
          type: "spring",
          stiffness: 500,
          damping: 45,
        }}
      >
        <ActionProvider>

          <TopBar user={user} signout={logout} />

          <>
            {isConnecting && <LoadingPage noText />}
            {user ? (
              <Switch>
                <Route path="/home">
                  <Home user={user} page={MOONLIGHT_PAGE.HOME} />;
                </Route>
                <Route path="/create-room">
                  <Home user={user} page={MOONLIGHT_PAGE.CREATE_ROOM} />;
                </Route>
                <Route path="/signin">
                  <AuthPage state={AuthFormState.LOGIN} />
                </Route>
                <Route path="/signup">
                  <AuthPage state={AuthFormState.REGISTER} />
                </Route>
                <Route path="/signout">
                  <AuthPage state={AuthFormState.SIGNOUT} />
                </Route>
                <Route path="/editor">
                  <Spreaditor />
                </Route>
                <Route path="/room/:roomId">
                  {(params) => <Room id={params.roomId} user={user} />}
                </Route>
                <Route path="/profile/:username">
                  {(params) => <ProfilePage username={params.username} />}
                </Route>
                <Route path="/session/:sessionId">
                  {(params) => <SessionView id={params.sessionId} />}
                </Route>

                <Route path="/">
                  <Home page={MOONLIGHT_PAGE.HOME} user={user} />
                </Route>
                <Route path="/leave">
                  <PostLeavingSurvey
                    email={user.email}
                    name={user.name}
                    username={user.username}
                    dest={window.location.host + "/"}
                  />
                </Route>
                <Route path="/decks/:deckPageId">
                  {(params) => (
                    <Home
                      user={user}
                      page={MOONLIGHT_PAGE.DECKS}
                      deckPageId={params.deckPageId}
                    />
                  )}
                </Route>
                <Route path="/decks">
                  <Home user={user} page={MOONLIGHT_PAGE.DECKS} />
                </Route>

                <Route path="/pros">
                  <RedirectPageWithKnownRef href={"/book"} />
                </Route>
                <Route path="/book">
                  <Home user={user} page={MOONLIGHT_PAGE.BOOK} />;
                </Route>
                <Route path="/availability">
                  <Home user={user} page={MOONLIGHT_PAGE.AVAILABILITY} />;
                </Route>
                <Route path="/sessions">
                  <Home user={user} page={MOONLIGHT_PAGE.UPCOMING} />;
                </Route>
                <Route path="/invites">
                  <Home user={user} page={MOONLIGHT_PAGE.REFERRALS} />;
                </Route>
                <Route path="/journal/:entryId">
                  {(params) => <Home user={user} page={MOONLIGHT_PAGE.JOURNAL} journalEntryId={params.entryId} />}
                </Route>
                <Route path="/journal">
                  <Home user={user} page={MOONLIGHT_PAGE.JOURNAL} />
                </Route>
                <Route path="/roulette">
                  <Home user={user} page={MOONLIGHT_PAGE.ROULETTE} />
                </Route>
                <Route path="/daily-spread-list">
                  <DailySpreadList />
                </Route>
                <Route path="/guidebook-list">
                  <GuidebookList />
                </Route>
                <Route path="/guidebook/:slug">
                  <Home user={user} page={MOONLIGHT_PAGE.GUIDEBOOK} />
                </Route>
                <Route path="/guidebook">
                  <Home user={user} page={MOONLIGHT_PAGE.GUIDEBOOK} />
                </Route>
                <Route path="/daily-spread">
                  <DailySpreadPage />
                </Route>
                <Route path="/game">
                  <GamePage />
                </Route>
                <Route path="/post/:postId">
                  {(params) => <CommunityPostDetail postId={params.postId} />}
                </Route>
                <Route path="/community">
                  <Home user={user} page={MOONLIGHT_PAGE.COMMUNITY} />
                </Route>

                <Route path="/plans">
                  <BillingPage />
                </Route>

                <Route path="/success">
                  <Success />
                </Route>
                <Route path="/session-action">
                  <SessionAction />
                </Route>
                <Route path="/session/video/:sessionId">
                  {(params) => (
                    <SessionVideoPlayer
                      id={params.sessionId ? params.sessionId : ""}
                    />
                  )}
                </Route>
                <Route path="/session/:sessionId">
                  {(params) => <SessionView id={params.sessionId} />}
                </Route>
                <Route path="/callback/oauth2">
                  <Oauth2Callback user={user} />
                </Route>
                <Route path="/404">
                  <PageNotFound />
                </Route>
                <Route path="/room404">
                  <RoomNotFound />
                </Route>

                <Route>
                  <RedirectPage />
                </Route>
              </Switch>
            ) : (
              <Switch>
                <Route path="/">
                  <Home user={user} page={MOONLIGHT_PAGE.HOME} />;
                </Route>
                <Route path="/create-room">
                  <Home user={user} page={MOONLIGHT_PAGE.CREATE_ROOM} />;
                </Route>
                <Route path="/signin">
                  <AuthPage state={AuthFormState.LOGIN} />
                </Route>
                <Route path="/signup">
                  <AuthPage state={AuthFormState.REGISTER} />
                </Route>
                <Route path="/forgotPassword">
                  <AuthPage state={AuthFormState.FORGOTPASSWORD} />
                </Route>
                <Route path="/resetPassword">
                  <AuthPage state={AuthFormState.RESETPASSWORD} />
                </Route>
                <Route path="/session-action">
                  <SessionAction />
                </Route>
                <Route path="/room/:roomId">
                  {(params) => <Room id={params.roomId} />}
                </Route>
                <Route path="/profile/:username">
                  {(params) => <ProfilePage username={params.username} />}
                </Route>
                <Route path="/session/video/:sessionId">
                  {(params) => (
                    <SessionVideoPlayer
                      id={params.sessionId ? params.sessionId : ""}
                    />
                  )}
                </Route>
                <Route path="/session/:sessionId">
                  {(params) => <SessionView id={params.sessionId} />}
                </Route>
                <Route path="/plans">
                  <BillingPage />
                </Route>
                <Route path="/decks/:deckPageId">
                  {(params) => (
                    <Home
                      user={user}
                      page={MOONLIGHT_PAGE.DECKS}
                      deckPageId={params.deckPageId}
                    />
                  )}
                </Route>
                <Route path="/decks">
                  <Home user={user} page={MOONLIGHT_PAGE.DECKS} />;
                </Route>

                <Route path="/pros">
                  <RedirectPageWithKnownRef href={"/book"} />
                </Route>
                <Route path="/sessions">
                  <RedirectPageWithKnownRef href={"/signin"} />
                </Route>
                <Route path="/book">
                  <Home user={user} page={MOONLIGHT_PAGE.BOOK} />;
                </Route>
                <Route path="/success">
                  <Success />
                </Route>
                <Route path="/404">
                  <PageNotFound />
                </Route>
                <Route path="/journal/:entryId">
                  {(params) => <Home user={user} page={MOONLIGHT_PAGE.JOURNAL} journalEntryId={params.entryId} />}
                </Route>
                <Route path="/journal">
                  <Home user={user} page={MOONLIGHT_PAGE.JOURNAL} />
                </Route>
                <Route path="/roulette">
                  <Home user={user} page={MOONLIGHT_PAGE.ROULETTE} />;
                </Route>
                <Route path="/guidebook/:slug">
                  <Home user={user} page={MOONLIGHT_PAGE.GUIDEBOOK} />
                </Route>
                <Route path="/guidebook">
                  <Home user={user} page={MOONLIGHT_PAGE.GUIDEBOOK} />
                </Route>
                <Route path="/daily-spread">
                  <RedirectPageWithKnownRef href="/signup?dest=/daily-spread" />
                </Route>
                <Route path="/game">
                  <GamePage />
                </Route>
                <Route path="/post/:postId">
                  <RedirectPageWithKnownRef href="/signup?dest=/community" />
                </Route>
                <Route path="/community">
                  <Home user={user} page={MOONLIGHT_PAGE.COMMUNITY} />
                </Route>
                <Route>
                  <RedirectPage />
                </Route>
              </Switch>
            )}
          </>
        </ActionProvider>
      </MotionConfig>
    </div>
  );
}

export default App;
